package com.siriusxm.pia.components

import androidx.compose.runtime.*
import com.siriusxm.cmc.utils.Navigator
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.SXMUI.borderDividerDefault
import com.siriusxm.pia.SXMUI.containerContentBackgroundColor
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.ExperimentalComposeWebApi
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Text

object ServiceViewStyles : StyleSheet() {
    val entityView by style {
        type("H1") style {
            fontWeight(800)
        }
    }

    val header by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Row)
        width(100.percent)
        backgroundColor(containerContentBackgroundColor.value())
        padding(10.px, 16.px)
        height(2.8.em)
        alignItems(AlignItems.Center)
        border {
            color = borderDividerDefault.value()
            style(LineStyle.Solid)
        }
        borderWidth(0.px, 0.px, 1.px, 0.px)

        desc(self, type("h3")).style {
            margin(0.px, 1.cssRem)
        }
    }

    val titleArea by style {
        marginTop(5.px)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        marginBottom(20.px)

        padding(0.px, 10.px, 0.px)
        whenWiderThanPhone {
            padding(0.px)
        }
    }

    val subHead by style {
        marginTop(5.px)
    }

    val sidebarIcon by style {
        padding(3.px)
        textAlign("center")
    }
    val sidebarIconSelected by style {
        backgroundColor(SXMUI.buttonBackgroundPrimary.value())
        borderRadius(50.percent)
        color(SXMUI.buttonForegroundPrimary.value())
    }

    @OptIn(ExperimentalComposeWebApi::class)
    val sidebar by style {
        position(Position.Relative)
        flexShrink(0)
        backgroundColor(containerContentBackgroundColor.value())
        border {
            color(borderDividerDefault.value())
            style(LineStyle.Solid)
        }
        overflow("hidden")

        transitions {
            "width" {
                duration(0.12.s)
                timingFunction(AnimationTimingFunction.Ease)
            }
        }

        child(self, type("h3")).style {
            margin(0.px)
            padding(1.cssRem, 1.cssRem, 0.cssRem)
        }
    }
    val sidebarClosed by style {
        width(0.px)
    }

    val sidebarExpanded by style {
        width(250.px)
    }


    val primaryContent by style {
        padding(0.px)
        width(100.percent)
        position(Position.Relative)

        whenWiderThanPhone {
            padding(10.px, 20.px)
        }
    }

    val simpleNavContainer by style {
        padding(0.px, .5.em)
        margin(2.em, 0.px)
    }

    val simpleNavList by style {
        listStyle("none")
        margin(0.px)
        padding(0.px)
    }

    val simpleNavListItem by style {
        margin(0.px)
        padding(8.px)
        borderRadius(5.px)

        type("a").style {
            color(SXMUI.subtleTextColor.value())
        }
    }

    val simpleNavSelected by style {
        fontWeight(700)
        backgroundColor(SXMUI.containerHeaderBackgroundColor.value())
        type("a").style {
            color(SXMUI.brandColor.value())
        }
    }
}

/**
 * Context provided to content rendered within a ServiceView. Provides access to
 * breadcrumb configuration and optional navigation capabilities.
 */
interface ServiceViewContext {
    /**
     * Optional navigator for handling navigation. When provided (typically in iframe-embedded
     * applications), breadcrumb clicks will use this navigator to update the parent window's URL.
     */
    val navigator: Navigator?

    /**
     * Configure the breadcrumbs displayed in the service view header.
     */
    fun breadCrumbs(block: (BreadcrumbConfiguration.() -> Unit)?)
}

/**
 * Builder class for the entity view
 */
abstract class ServiceViewConfiguration {
    internal var content: (@Composable ServiceViewContext.() -> Unit)? = null
    internal var sidebar: (@Composable () -> Unit)? = null
    internal var help: (@Composable () -> Unit)? = null
    internal var sidebarVisible = false
    internal var sidebarTitle: String? = null

    internal var breadcrumbConfig: (BreadcrumbConfiguration.() -> Unit)? = null

    var title: String? = null

    /**
     * Optional navigator for handling breadcrumb navigation. When provided (typically in
     * iframe-embedded applications), breadcrumb clicks will use this navigator to update
     * the parent window's URL instead of using standard hash-based navigation.
     */
    var navigator: Navigator? = null

    fun content(block: @Composable ServiceViewContext.() -> Unit) {
        content = block
    }

    /**
     * Install a sidebar. Show/hide will be managed by this view.
     */
    fun navigationSidebar(defaultVisibility: Boolean = false, title: String? = null, block: @Composable () -> Unit) {
        sidebar = block
        sidebarVisible = defaultVisibility
        sidebarTitle = title
    }

    abstract fun hideSidebar()

    /**
     * Install a help sidebar. Show/hide will be managed by this view.
     */
    fun helpSidebar(block: @Composable () -> Unit) {
        help = block
    }
}

enum class SideBarSide {
    LEFT,
    RIGHT
}

@Composable
fun sideBar(side: SideBarSide, title: String?, visible: Boolean, onClose: () -> Unit, content: @Composable () -> Unit) {
    Div({
        classes(
            ServiceViewStyles.sidebar,
            if (visible) ServiceViewStyles.sidebarExpanded else ServiceViewStyles.sidebarClosed
        )
        style {
            if (side == SideBarSide.LEFT) {
                borderWidth(0.px, 1.px, 0.px, 0.px)
            } else {
                borderWidth(0.px, 0.px, 0.px, 1.px)
            }
        }
    }) {
        Div({
            style {
                position(Position.Absolute)
                right(5.px)
                top(5.px)
            }
        }) {
            icon("close") {
                size = IconSize.SMALL
                action {
                    onClose()
                }
            }
        }
        if (title != null) {
            H3 {
                Text(title)
            }
        }
        content()
    }
}

/**
 * The service view is the main view for a service. It includes an optional left navigation, and
 * standard spaced content area.
 */
@Composable
fun serviceView(config: InternalServiceViewConfiguration) {
    val scope = rememberCoroutineScope()
    var breadcrumbConfig by remember { mutableStateOf<(BreadcrumbConfiguration.() -> Unit)?>(null) }

    // Create the context object once, capturing the navigator from config
    val configNavigator = config.navigator
    val serviceViewContext = remember(configNavigator) {
        object : ServiceViewContext {
            override val navigator: Navigator? = configNavigator

            override fun breadCrumbs(block: (BreadcrumbConfiguration.() -> Unit)?) {
                scope.launch {
                    breadcrumbConfig = block
                }
            }
        }
    }

    Column {
        var sidebarVisible by remember { mutableStateOf(config.sidebarVisible) }
        var helpVisible by remember { mutableStateOf(false) }

        config.sidebarHandler = {
            sidebarVisible = false
        }

        if (config.sidebar != null || config.help != null || config.title != null) {
            Div({ classes(ServiceViewStyles.header) }) {
                if (config.sidebar != null) {
                    Div({
                        classes(ServiceViewStyles.sidebarIcon)
                        if (sidebarVisible) {
                            classes(ServiceViewStyles.sidebarIconSelected)
                        }
                    }) {
                        icon("menu") {
                            size = IconSize.SMALL
                            action {
                                sidebarVisible = !sidebarVisible
                            }
                        }
                    }
                }
                Div({
                    style {
                        flex(1)
                    }
                }) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            flexDirection(FlexDirection.Row)
                            marginLeft(10.px)
                        }
                    }) {
                        if (breadcrumbConfig != null) {
                            breadcrumbs(config.navigator, breadcrumbConfig!!)
                        } else {
                            val title = config.sidebarTitle ?: config.title
                            if (title != null) {
                                breadcrumbs(config.navigator) {
                                    crumb(title, null)
                                }
                            }
                        }
                    }
                }
                config.help?.let { helpContent ->
                    Div({
                        classes(ServiceViewStyles.sidebarIcon)
                        if (helpVisible) {
                            classes(ServiceViewStyles.sidebarIconSelected)
                        }
                    }) {
                        icon("help") {
                            size = IconSize.SMALL
                            action {
                                helpVisible = !helpVisible
                            }
                        }
                    }
                }
            }
        }
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Stretch)
                flex(1, 1)
                flexBasis("auto")
                minHeight(0.px)
                width(100.percent)
            }
        }) {
            // Navigation sidebar
            config.sidebar?.let { content ->
                sideBar(SideBarSide.LEFT, null, sidebarVisible, {
                    sidebarVisible = false
                }, content)
            }

            // Main content
            Div({
                style {
                    flex(1)
                    minWidth(0.px)
                    minHeight(0.px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                }
            }) {
                config.content?.invoke(serviceViewContext)
            }

//            // Help sidebar
            config.help?.let { helpContent ->
                sideBar(SideBarSide.RIGHT, null, false, {
                    helpVisible = false
                }, helpContent)
            }
        }
    }
}

class InternalServiceViewConfiguration : ServiceViewConfiguration() {
    var sidebarHandler: (Boolean) -> Unit = { }
    override fun hideSidebar() {
        println("hideSidebar")
        sidebarHandler(false)
    }
}

/**
 * Renders a view of an entity. This generally includes a title, some actions and quite a bit
 * of content.
 */
@Composable
fun serviceView(builder: ServiceViewConfiguration.() -> Unit = {}, content: @Composable ServiceViewContext.() -> Unit) {
    val config = InternalServiceViewConfiguration().apply(builder)
    config.content = content
    serviceView(config)
}